import {useState, useEffect, useRef} from 'react';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {TournamentRound} from '@types';

dayjs.extend(duration);

type BrowserInterval = ReturnType<typeof window.setInterval>;

// Add delay in case the tournament not started yet in backend
const calcTimeInterval = (startedAt: string, reverse = false, delay = 0.5) => {
    const startTimestamp = new Date(startedAt).getTime() + delay * 1000;
    const now = Date.now();

    const timeInterval = reverse ? now - startTimestamp : startTimestamp - now;

    return Math.round(timeInterval / 1000);
};

export const useTournamentCountdown = (
    tournament?: {startedAt: string} | null
) => {
    const interval = useRef<BrowserInterval | null>(null);
    const [countdown, setCountdown] = useState(
        tournament?.startedAt
            ? calcTimeInterval(tournament?.startedAt)
            : Number.MAX_SAFE_INTEGER
    );

    useEffect(() => {
        if (!tournament || !tournament.startedAt) return;
        interval.current = setInterval(() => {
            setCountdown(calcTimeInterval(tournament.startedAt));
        }, 500);

        return () => {
            if (interval.current) clearInterval(interval.current);
        };
    }, [tournament]);

    useEffect(() => {
        if (countdown <= 0) {
            if (interval.current) {
                clearInterval(interval.current);
            }
            setCountdown(0);
        }
    }, [countdown]);

    if (!tournament || !tournament.startedAt) {
        return [Number.MAX_SAFE_INTEGER];
    }

    return [countdown];
};

export const useRoundCountdown = (
    round?: TournamentRound | null,
    defaultValue = Number.MAX_SAFE_INTEGER
) => {
    const interval = useRef<BrowserInterval | null>(null);
    const [countdown, setCountdown] = useState(
        round?.startedAt ? calcTimeInterval(round?.startedAt) : defaultValue
    );

    useEffect(() => {
        if (!round || !round.startedAt) return;
        interval.current = setInterval(() => {
            setCountdown(calcTimeInterval(round.startedAt));
        }, 500);

        return () => {
            if (interval.current) clearInterval(interval.current);
        };
    }, [round]);

    useEffect(() => {
        if (countdown <= 0) {
            if (interval.current) {
                clearInterval(interval.current);
                setCountdown(defaultValue);
            }
        }
    }, [countdown, defaultValue]);

    if (!round || !round.startedAt) {
        return [defaultValue];
    }

    return [countdown];
};

export const useRoundElapsedTime = (
    round?: TournamentRound | null,
    endTime = Number.MAX_SAFE_INTEGER
) => {
    const interval = useRef<BrowserInterval | null>(null);
    const [elapsedTime, setElapsedTime] = useState(
        round?.startedAt ? calcTimeInterval(round?.startedAt, true) : 0
    );

    useEffect(() => {
        if (!round?.startedAt) return;
        const updateElapsedTime = () => {
            const elapsedTime = calcTimeInterval(round.startedAt, true);
            setElapsedTime(elapsedTime);
        };
        updateElapsedTime();

        interval.current = setInterval(() => {
            updateElapsedTime();
        }, 500);

        return () => {
            if (interval.current) clearInterval(interval.current);
        };
    }, [round?.startedAt]);

    useEffect(() => {
        if (elapsedTime >= endTime) {
            if (interval.current) {
                clearInterval(interval.current);
            }
            setElapsedTime(endTime);
        }
    }, [elapsedTime, endTime]);

    if (!round || !round.startedAt) {
        return [0];
    }

    return [elapsedTime];
};
